import React, {
  useContext,
  useState,
  useEffect,
  useReducer,
  useRef,
  useCallback,
} from 'react';

import { isSameDay, parseISO, format } from 'date-fns';

import {
  Box,
  Button,
  CircularProgress,
  Divider,
  IconButton,
  useTheme,
} from '@mui/material';

import {
  AccessTime,
  Done,
  DoneAll,
  ExpandMore,
  GetApp,
  Facebook,
  Instagram,
  Reply,
  Block,
} from '@mui/icons-material';

import {
  FaFilePdf,
  FaFileWord,
  FaFileExcel,
  FaFilePowerpoint,
  FaFileImage,
  FaFileVideo,
  FaFileAudio,
  FaFileCode,
  FaFile,
  FaFileZipper,
  FaFileCsv,
} from 'react-icons/fa6';

import { GrAndroid, GrCertificate } from 'react-icons/gr';

import MarkdownWrapper from '../MarkdownWrapper';
import VcardPreview from '../VcardPreview';
import LocationPreview from '../LocationPreview';
import ModalImageCors from '../ModalImageCors';
import MessageOptionsMenu from '../MessageOptionsMenu';
import YouTubePreview from '../ModalYoutubeCors';
import ListPreview from '../ListPreview';
import PixPreview from '../PixPreview';
import ButtonPreview from '../ButtonPreview';
import EventPreview from '../EventPreview';
import SelectMessageCheckbox from './SelectMessageCheckbox';
import AudioModal from '../AudioModal';
import MessageReactionsModal from '../MessageReactionsModal';

import { ReplyMessageContext } from '../../context/ReplyingMessage/ReplyingMessageContext';
import { ForwardMessageContext } from '../../context/ForwarMessage/ForwardMessageContext';
import api from '../../services/api';
import toastError from '../../toasts/toastError';
import AdMetaPreview from '../AdMetaPreview';
import { i18n } from '../../translate/i18n';
import useCompanySettings from '../../hooks/useSettings/companySettings';
import { AuthContext } from '../../context/Auth/AuthContext';
import { QueueSelectedContext } from '../../context/QueuesSelected/QueuesSelectedContext';
import { useParams } from 'react-router-dom';
import { downloadResource } from '../../utils';
import '../../helpers/supressWarn';
import { getStyled } from './styles';

const getBasename = (url) => {
  if (!url) return '';
  return url.split('/').pop();
};

const XML_REGEX = /<([^>]+)>/g;

const isYouTubeLink = (url) => {
  const youtubeRegex =
    /^(?:https?:\/\/)?(?:www\.)?(?:youtube\.com\/(?:watch\?v=|embed\/|v\/)|youtu\.be\/)([a-zA-Z0-9_-]{11})/;
  return youtubeRegex.test(url);
};

const messagesReducer = (state, action) => {
  switch (action.type) {
    case 'LOAD_MESSAGES': {
      const incoming = action.payload;
      const stateMap = new Map(state.map((m) => [m.id, m]));
      incoming.forEach((m) => stateMap.set(m.id, m));
      return Array.from(stateMap.values()).sort((a, b) => a.id - b.id);
    }
    case 'ADD_MESSAGE': {
      const newMsg = action.payload;
      const exists = state.some((m) => m.id === newMsg.id);
      if (exists) {
        return state.map((m) => (m.id === newMsg.id ? newMsg : m));
      }
      return [...state, newMsg];
    }
    case 'UPDATE_MESSAGE': {
      return state.map((m) =>
        m.id === action.payload.id ? action.payload : m,
      );
    }
    case 'DELETE_MESSAGE': {
      return state.filter((m) => m.id !== action.payload);
    }
    case 'RESET': {
      return [];
    }
    default:
      return state;
  }
};

const MessagesList = ({ isGroup, onDrop, whatsappId, queueId, channel }) => {
  const theme = useTheme();
  const classes = getStyled(theme);

  const [messagesList, dispatch] = useReducer(messagesReducer, []);
  const [pageNumber, setPageNumber] = useState(1);
  const [hasMore, setHasMore] = useState(false);
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);

  const lastMessageRef = useRef();
  const messageRef = useRef({});
  const activeTicketIdRef = useRef(null);

  const [scrollToMessageId, setScrollToMessageId] = useState(null);
  const [selectedMessage, setSelectedMessage] = useState({});
  const { setReplyingMessage } = useContext(ReplyMessageContext);
  const [anchorEl, setAnchorEl] = useState(null);
  const messageOptionsMenuOpen = Boolean(anchorEl);
  const { ticketId } = useParams();

  const [reactionsSummary, setReactionsSummary] = useState({});
  const [showReactionSender, setShowReactionSender] = useState(false);
  const [selectedMessageForReactions, setSelectedMessageForReactions] =
    useState(null);
  const [hoveredMessageId, setHoveredMessageId] = useState(null);
  const [dragActive, setDragActive] = useState(false);
  const [lgpdDeleteMessage, setLGPDDeleteMessage] = useState(false);

  const { selectedQueuesMessage } = useContext(QueueSelectedContext);
  const { showSelectMessageCheckbox } = useContext(ForwardMessageContext);
  const { user, socket } = useContext(AuthContext);
  const { getAll } = useCompanySettings();

  const companyId = user.companyId;

  const fetchReactionSummary = useCallback(async (messageId, forTicketId) => {
    if (forTicketId && forTicketId !== activeTicketIdRef.current) return;
    try {
      const { data } = await api.get(
        `/messages/${messageId}/reactions/summary`,
      );
      setReactionsSummary((prev) => ({ ...prev, [messageId]: data }));
    } catch (error) {
      console.error('Erro ao buscar reações:', error);
    }
  }, []);

  const scrollToBottom = () => {
    setTimeout(() => {
      lastMessageRef.current?.scrollIntoView({});
    }, 150);
  };

  useEffect(() => {
    if (!ticketId) {
      activeTicketIdRef.current = null;
      dispatch({ type: 'RESET' });
      return;
    }

    activeTicketIdRef.current = ticketId;
    dispatch({ type: 'RESET' });
    setPageNumber(1);
    setHasMore(false);
    setReactionsSummary({});
    messageRef.current = {};
  }, [ticketId]);

  useEffect(() => {
    setPageNumber(1);
  }, [selectedQueuesMessage]);

  useEffect(() => {
    const fetchSettings = async () => {
      const settings = await getAll(companyId);
      const lgpdDelete = settings['lgpdDeleteMessage'];
      const enableLGPD = settings['enableLGPD'];
      if (enableLGPD === 'enabled' && lgpdDelete === 'enabled') {
        setLGPDDeleteMessage(true);
      }
    };
    fetchSettings();
  }, [companyId, getAll]);

  useEffect(() => {
    if (!ticketId) return;

    const controller = new AbortController();
    const snapshotTicketId = ticketId;

    const fetchMessages = async () => {
      setLoading(true);
      try {
        const { data } = await api.get('/messages/' + snapshotTicketId, {
          signal: controller.signal,
          params: {
            pageNumber,
            selectedQueues: JSON.stringify(selectedQueuesMessage),
          },
        });

        if (activeTicketIdRef.current !== snapshotTicketId) return;

        dispatch({ type: 'LOAD_MESSAGES', payload: data.messages });
        setHasMore(data.hasMore);

        data.messages.forEach((message) => {
          fetchReactionSummary(message.id, snapshotTicketId);
        });

        if (pageNumber === 1 && data.messages.length > 1) {
          scrollToBottom();
        }
      } catch (err) {
        if (err.name === 'CanceledError' || err.name === 'AbortError') return;
        toastError(err);
      } finally {
        if (activeTicketIdRef.current === snapshotTicketId) {
          setLoading(false);
          setLoadingMore(false);
        }
      }
    };

    fetchMessages();

    return () => {
      controller.abort();
    };
  }, [pageNumber, ticketId, selectedQueuesMessage]); //eslint-disable-line

  useEffect(() => {
    if (!ticketId) return;
    if (!socket || typeof socket.on !== 'function') {
      console.warn('Socket não disponível em MessagesList');
      return;
    }

    const snapshotTicketId = ticketId;

    const handleConnect = () => {
      socket.emit('joinChatBox', snapshotTicketId);
    };

    const handleAppMessage = (data) => {
      const eventTicketUuid =
        data.ticket?.uuid ?? data.message?.ticket?.uuid ?? null;

      if (!eventTicketUuid) return;
      if (eventTicketUuid !== snapshotTicketId) return;
      if (activeTicketIdRef.current !== snapshotTicketId) return;

      if (data.action === 'create') {
        dispatch({ type: 'ADD_MESSAGE', payload: data.message });
        fetchReactionSummary(data.message.id, snapshotTicketId);
        scrollToBottom();
      } else if (data.action === 'update') {
        dispatch({ type: 'UPDATE_MESSAGE', payload: data.message });
      } else if (data.action === 'delete') {
        dispatch({ type: 'DELETE_MESSAGE', payload: data.messageId });
      }
    };

    const handleMessageReaction = (data) => {
      if (activeTicketIdRef.current !== snapshotTicketId) return;
      if (data.messageId) {
        fetchReactionSummary(data.messageId, snapshotTicketId);
      }
    };

    socket.emit('joinChatBox', snapshotTicketId);
    socket.on('connect', handleConnect);
    socket.on(`company-${companyId}-appMessage`, handleAppMessage);
    socket.on(`company-${companyId}-message-reaction`, handleMessageReaction);

    return () => {
      socket.emit('joinChatBoxLeave', snapshotTicketId);
      socket.off('connect', handleConnect);
      socket.off(`company-${companyId}-appMessage`, handleAppMessage);
      socket.off(
        `company-${companyId}-message-reaction`,
        handleMessageReaction,
      );
    };
  }, [ticketId, socket, companyId, fetchReactionSummary]);

  useEffect(() => {
    if (scrollToMessageId && messageRef.current?.[scrollToMessageId]) {
      messageRef.current[scrollToMessageId].scrollIntoView();
      setScrollToMessageId(null);
    }
  }, [scrollToMessageId]);

  const loadMore = useCallback(() => {
    if (loadingMore) return;
    setLoadingMore(true);
    setPageNumber((prev) => prev + 1);
  }, [loadingMore]);

  const handleScroll = useCallback(
    (e) => {
      if (!hasMore || loading) return;
      const { scrollTop } = e.currentTarget;
      if (scrollTop === 0) {
        e.currentTarget.scrollTop = 1;
      }
      if (scrollTop < 50) {
        loadMore();
      }
    },
    [hasMore, loading, loadMore],
  );

  const scrollToMessage = useCallback(
    async (id) => {
      if (messageRef.current?.[id]) {
        messageRef.current[id].scrollIntoView({ behavior: 'smooth' });
        return;
      }

      const snapshotTicketId = ticketId;
      let currentPage = 1;

      while (true) {
        try {
          const { data } = await api.get('/messages/' + snapshotTicketId, {
            params: {
              pageNumber: currentPage,
              selectedQueues: JSON.stringify(selectedQueuesMessage),
            },
          });

          if (activeTicketIdRef.current !== snapshotTicketId) return;

          if (data.messages.some((m) => m.id === parseInt(id))) {
            setScrollToMessageId(id);
            setPageNumber(currentPage);
            return;
          }

          if (!data.hasMore) break;
          currentPage++;
        } catch (error) {
          console.error(error);
          break;
        }
      }
    },
    [ticketId, selectedQueuesMessage],
  );

  const handleOpenMessageOptionsMenu = useCallback((e, message) => {
    setSelectedMessage(message);
    setAnchorEl(e.currentTarget);
  }, []);

  const handleCloseMessageOptionsMenu = useCallback(() => {
    setAnchorEl(null);
    setSelectedMessage({});
  }, []);

  const handleReplyMessage = useCallback(
    (message) => {
      setAnchorEl(null);
      setReplyingMessage(message);
    },
    [setReplyingMessage],
  );

  const handleShowReactionSenders = useCallback((message) => {
    setSelectedMessageForReactions(message);
    setShowReactionSender(true);
  }, []);

  const handleCloseReactionModal = useCallback(() => {
    setShowReactionSender(false);
    setSelectedMessageForReactions(null);
  }, []);

  const handleDrag = useCallback((event) => {
    event.preventDefault();
    event.stopPropagation();
    setDragActive(event.type === 'dragenter' || event.type === 'dragover');
  }, []);

  const handleDrop = useCallback(
    (event) => {
      event.preventDefault();
      event.stopPropagation();
      setDragActive(false);
      if (event.dataTransfer.files?.[0]) {
        onDrop?.(event.dataTransfer.files);
      }
    },
    [onDrop],
  );

  const getFileExtension = (url) => {
    if (!url) return 'file';
    return url.split('.').pop()?.toLowerCase() || 'file';
  };

  const getFileIcon = (url) => {
    if (!url) return <FaFile size={48} color={theme.custom.colors.text} />;

    const ext = url.toLowerCase().split('.').pop();
    const color = theme.custom.colors.text;

    const iconMap = {
      pdf: <FaFilePdf size={48} color={color} />,
      doc: <FaFileWord size={48} color={color} />,
      docx: <FaFileWord size={48} color={color} />,
      xls: <FaFileExcel size={48} color={color} />,
      xlsx: <FaFileExcel size={48} color={color} />,
      csv: <FaFileCsv size={48} color={color} />,
      ppt: <FaFilePowerpoint size={48} color={color} />,
      pptx: <FaFilePowerpoint size={48} color={color} />,
      jpg: <FaFileImage size={48} color={color} />,
      jpeg: <FaFileImage size={48} color={color} />,
      png: <FaFileImage size={48} color={color} />,
      gif: <FaFileImage size={48} color={color} />,
      mp4: <FaFileVideo size={48} color={color} />,
      avi: <FaFileVideo size={48} color={color} />,
      mkv: <FaFileVideo size={48} color={color} />,
      mp3: <FaFileAudio size={48} color={color} />,
      wav: <FaFileAudio size={48} color={color} />,
      zip: <FaFileZipper size={48} color={color} />,
      rar: <FaFileZipper size={48} color={color} />,
      js: <FaFileCode size={48} color={color} />,
      ts: <FaFileCode size={48} color={color} />,
      html: <FaFileCode size={48} color={color} />,
      css: <FaFileCode size={48} color={color} />,
      json: <FaFileCode size={48} color={color} />,
      txt: <FaFile size={48} color={color} />,
      apk: <GrAndroid size={48} color={color} />,
      p12: <GrCertificate size={48} color={color} />,
      pfx: <GrCertificate size={48} color={color} />,
    };

    return iconMap[ext] ?? <FaFile size={48} color={color} />;
  };

  const checkMessageMedia = (message) => {
    switch (message.mediaType) {
      case 'eventMessage': {
        try {
          const {
            message: { eventMessage },
          } = JSON.parse(message.dataJson);
          const startTime = Number(eventMessage.startTime);
          const link = eventMessage.joinLink || '#';
          return (
            <EventPreview
              name={eventMessage.name || 'Sem título'}
              description={eventMessage.description || 'Sem descrição'}
              location={eventMessage.location?.name || 'Local não especificado'}
              startTime={startTime}
              endTime={startTime + 7200}
              joinLink={link}
              linkType={link.includes('video') ? 'video' : 'voice'}
            />
          );
        } catch (e) {
          console.error('Erro ao processar eventMessage:', e);
          return null;
        }
      }

      case 'listMessage': {
        try {
          const {
            message: { listMessage },
          } = JSON.parse(message.dataJson);
          const secoes = listMessage.sections.map((section) => ({
            titulo: section.title || '',
            linhas: section.rows.map((row) => ({
              titulo: row.title,
              descricao: row.description,
              idLinha: row.rowId,
            })),
          }));
          return (
            <ListPreview
              titulo={listMessage.title || ''}
              descricao={listMessage.description || ''}
              textoBotao={listMessage.buttonText || 'Clique aqui'}
              secoes={secoes}
              rodape={listMessage.footerText || ''}
              ticketId={message?.ticket?.id}
            />
          );
        } catch (e) {
          console.error('Erro ao processar listMessage:', e);
          return null;
        }
      }

      case 'viewOnceMessage': {
        if (message.body.startsWith('[BOTOES]')) {
          try {
            const parsed = JSON.parse(message.dataJson);
            const interactive =
              parsed.message.viewOnceMessage.message.interactiveMessage;
            const botoes =
              interactive.nativeFlowMessage?.buttons?.map((btn) => {
                const params = JSON.parse(btn.buttonParamsJson);
                return {
                  tipo: btn.name,
                  texto: params.display_text,
                  conteudo:
                    params.phoneNumber || params.url || params.copy_code,
                };
              }) ?? [];
            return (
              <ButtonPreview
                titulo={interactive.body?.text || ''}
                rodape={interactive.footer?.text || ''}
                secoes={[{ titulo: 'Botões', linhas: botoes }]}
                imagem={interactive.header?.imageMessage?.jpegThumbnail ?? null}
                ticketId={message?.ticket?.id}
              />
            );
          } catch (e) {
            console.error('Erro ao processar viewOnceMessage [BOTOES]:', e);
            return null;
          }
        }
        if (message.body.startsWith('[PIX]')) {
          return renderPixMessage(message);
        }
        return null;
      }

      case 'interactiveMessage': {
        if (message.body.startsWith('[PIX]')) {
          return renderPixMessage(message);
        }
        return null;
      }

      case 'locationMessage': {
        const parts = message.body.split('|');
        if (parts.length < 2) return null;
        return (
          <LocationPreview
            image={parts[0]}
            link={parts[1]}
            description={parts[2] ?? null}
          />
        );
      }

      case 'contactMessage': {
        const lines = message.body.split('\n');
        let contact = '';
        let number = '';
        lines.forEach((line) => {
          const parts = line.split(':');
          parts.forEach((part, i) => {
            if (part.includes('+')) number = part;
            if (part.includes('FN')) contact = parts[i + 1] || '';
          });
        });
        return (
          <VcardPreview
            contact={contact}
            numbers={number}
            queueId={message?.ticket?.queueId}
            whatsappId={message?.ticket?.whatsappId}
          />
        );
      }

      case 'adMetaPreview': {
        const [image, sourceUrl, title, body, messageUser] =
          message.body.split('|');
        return (
          <AdMetaPreview
            image={image}
            sourceUrl={sourceUrl}
            title={title}
            body={body}
            messageUser={messageUser}
          />
        );
      }

      case 'image':
        return <ModalImageCors imageUrl={message.mediaUrl} />;

      case 'audio':
        return <AudioModal url={message.mediaUrl} />;

      case 'video':
        return (
          <video style={classes.messageMedia} src={message.mediaUrl} controls />
        );

      case 'application': {
        const isPdf =
          message.mediaUrl?.toLowerCase().endsWith('.pdf') ||
          message.body?.toLowerCase().endsWith('.pdf');
        if (isPdf) {
          return (
            <>
              <div style={classes.downloadMedia}>
                <iframe
                  src={message.mediaUrl || message.body}
                  title='Prévia do PDF'
                  width='100%'
                  height='200px'
                  style={{ border: 'none', borderRadius: 8 }}
                />
              </div>
              <div style={classes.downloadMedia}>
                <Button
                  startIcon={<GetApp />}
                  variant='outlined'
                  sx={theme.custom.editButton}
                  onClick={() => {
                    const isPfx = message.documentMessage?.fileName
                      ?.toLowerCase()
                      .endsWith('.pfx');
                    downloadResource(
                      message.mediaUrl || message.body,
                      isPfx ? message.documentMessage.fileName : null,
                    );
                  }}
                >
                  Download
                </Button>
              </div>
              <Divider />
            </>
          );
        }
        break;
      }

      default:
        break;
    }

    return (
      <>
        <div
          style={{
            ...classes.downloadMedia,
            display: 'flex',
            flexDirection: 'column',
          }}
        >
          <div style={{ textAlign: 'center', padding: '10px' }}>
            {getFileIcon(message.mediaUrl)}
            <div
              style={{
                marginTop: 8,
                fontSize: 12,
                color: theme.custom.colors.text,
              }}
            >
              {getFileExtension(message.mediaUrl).toUpperCase()}
            </div>
          </div>
          <Button
            startIcon={<GetApp />}
            sx={theme.custom.editButton}
            variant='outlined'
            onClick={() => downloadResource(message.mediaUrl || message.body)}
          >
            Download
          </Button>
        </div>
        <Divider />
      </>
    );
  };

  const renderPixMessage = (message) => {
    try {
      const parsed = JSON.parse(message.dataJson);
      const nativeFlowMessage =
        parsed.message?.interactiveMessage?.nativeFlowMessage ||
        parsed.message?.viewOnceMessage?.message?.interactiveMessage
          ?.nativeFlowMessage;
      if (!nativeFlowMessage) return null;

      const button = nativeFlowMessage.buttons?.[0];
      if (!button) return null;

      const params = JSON.parse(button.buttonParamsJson);
      return (
        <PixPreview
          companyId={message.companyId}
          avatarUser={message.ticket?.user?.profileImage}
          avatarName={message.ticket?.user?.name}
          avatarUrl={message.contact?.urlPicture}
          name={message.contact?.name}
          numeroCobranca={params.reference_id || 'N/A'}
          total={params.total_amount?.value || 'N/A'}
          produto={params.order?.items?.[0]?.name || 'N/A'}
          imagem={nativeFlowMessage.header?.imageMessage?.jpegThumbnail ?? null}
          ticketId={message?.ticket?.id}
        />
      );
    } catch (e) {
      console.error('Erro ao processar mensagem PIX:', e);
      return null;
    }
  };

  const renderMessageAck = (message) => {
    const ackMap = {
      0: <AccessTime fontSize='small' style={classes.ackIcons} />,
      1: <Done fontSize='small' style={classes.ackIcons} />,
      2: <DoneAll fontSize='small' style={classes.ackIcons} />,
      3: (
        <DoneAll
          fontSize='small'
          sx={
            message.mediaType === 'audio'
              ? classes.ackPlayedIcon
              : classes.ackDoneAllIcon
          }
        />
      ),
      4: (
        <DoneAll
          fontSize='small'
          sx={
            message.mediaType === 'audio'
              ? classes.ackPlayedIcon
              : classes.ackDoneAllIcon
          }
        />
      ),
      5: <DoneAll fontSize='small' style={classes.ackDoneAllIcon} />,
    };
    return ackMap[message.ack] ?? null;
  };

  const renderDailyTimestamps = (message, index) => {
    const today = format(new Date(), 'dd/MM/yyyy');
    const msgDate = format(parseISO(message.createdAt), 'dd/MM/yyyy');
    const label = msgDate === today ? 'HOJE' : msgDate;

    const isFirst = index === 0;
    const isLast = index === messagesList.length - 1;

    if (isFirst) {
      return (
        <span style={classes.dailyTimestamp} key={`timestamp-${message.id}`}>
          <div style={classes.dailyTimestampText}>{label}</div>
        </span>
      );
    }

    if (!isLast) {
      const prevDate = parseISO(messagesList[index - 1].createdAt);
      const currDate = parseISO(message.createdAt);
      if (!isSameDay(currDate, prevDate)) {
        return (
          <span style={classes.dailyTimestamp} key={`timestamp-${message.id}`}>
            <div style={classes.dailyTimestampText}>{label}</div>
          </span>
        );
      }
    }

    if (isLast) {
      return (
        <div
          key={`ref-${message.id}`}
          ref={lastMessageRef}
          style={{ float: 'left', clear: 'both' }}
        />
      );
    }

    return null;
  };

  const renderTicketsSeparator = (message, index) => {
    if (index === 0) return null;
    const lastTicketId = messagesList[index - 1]?.ticketId;
    if (lastTicketId === message.ticketId) return null;

    const queueName = message?.ticket?.queue?.name;
    const label = queueName
      ? `#${i18n.t('ticketsList.called')} ${message.ticketId} - ${queueName}`
      : `#${i18n.t('ticketsList.called')} ${message.ticketId} - ${i18n.t('ticketsList.noQueue')}`;

    return (
      <span style={classes.currentTick} key={`ticket-sep-${message.id}`}>
        <div style={classes.currentTicktText}>{label}</div>
      </span>
    );
  };

  const renderMessageDivider = (message, index) => {
    if (index === 0) return null;
    if (messagesList[index].fromMe !== messagesList[index - 1].fromMe) {
      return <span style={{ marginTop: 16 }} key={`divider-${message.id}`} />;
    }
    return null;
  };

  const renderQuotedMessage = (message) => {
    if (!message.quotedMsg) return null;
    const quoted = message.quotedMsg;

    return (
      <div
        onClick={() => scrollToMessage(quoted.id)}
        style={{
          ...classes.quotedContainerLeft,
          ...(message.fromMe ? classes.quotedContainerRight : {}),
          cursor: 'pointer',
        }}
      >
        <span
          style={{
            ...classes.quotedSideColorLeft,
            ...(quoted.fromMe ? classes.quotedSideColorRight : {}),
          }}
        />
        <div style={classes.quotedMsg}>
          {!quoted.fromMe && (
            <span style={classes.messageContactName}>
              {quoted.contact?.name}
            </span>
          )}
          {quoted.mediaType === 'audio' && (
            <div style={classes.downloadMedia}>
              <AudioModal url={quoted.mediaUrl} />
            </div>
          )}
          {quoted.mediaType === 'video' && (
            <video
              style={classes.messageMedia}
              src={quoted.mediaUrl}
              controls
            />
          )}
          {quoted.mediaType === 'contactMessage' && 'Contato'}
          {quoted.mediaType === 'application' && (
            <div style={classes.downloadMedia}>
              <iframe
                src={message.mediaUrl || message.body}
                title='Prévia do arquivo'
                width='100%'
                height='200px'
                style={{ border: 'none', borderRadius: 8 }}
              />
              <Button
                startIcon={<GetApp />}
                sx={theme.custom.editButton}
                variant='outlined'
                href={quoted.mediaUrl}
                target='_blank'
              >
                Download
              </Button>
            </div>
          )}
          {quoted.mediaType === 'image' ? (
            <ModalImageCors imageUrl={quoted.mediaUrl} />
          ) : (
            quoted.body
          )}
        </div>
      </div>
    );
  };

  const MessageReactions = ({ message, position }) => {
    const reactions = reactionsSummary[message.id];
    if (!reactions || reactions.count === 0) return null;

    return (
      <div
        style={
          position === 'left' ? classes.reactionLeft : classes.reactionRight
        }
        onClick={() => handleShowReactionSenders(message)}
      >
        {reactions.reactions.map((emoji, index) => (
          <span
            key={index}
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: '12px',
            }}
          >
            {emoji}
          </span>
        ))}
        {reactions.count > 1 &&
          reactions.count > reactions.reactions.length && (
            <span
              style={{
                marginLeft: 4,
                marginRight: 2,
                fontSize: 12,
                fontWeight: 'lighter',
              }}
            >
              {reactions.count}
            </span>
          )}
      </div>
    );
  };

  const fetchHasReaction = (message) =>
    (reactionsSummary[message.id]?.count ?? 0) > 0;

  const TransferMessage = ({ message }) => (
    <div style={{ display: 'flex', flexDirection: 'column' }}>
      <div>
        Transferido por: <b>{message.userTransfered?.name}</b> 🔄 Transferido
        para:{' '}
        <b>
          {message.transferedToId ? message.transferedToUser?.name : 'PENDENTE'}
        </b>
      </div>
      <span style={{ alignSelf: 'center' }}>{message.body}</span>
    </div>
  );

  const renderMessageText = (message, isFromMe) => {
    if (XML_REGEX.test(message.body)) {
      return <span>{message.body}</span>;
    }

    if (
      isFromMe &&
      message.isPrivate &&
      message.userTransferedId &&
      classes.messageRightPrivate
    ) {
      return <TransferMessage message={message} />;
    }

    return (
      <MarkdownWrapper>
        {lgpdDeleteMessage && message.isDeleted
          ? '🚫 Mensagem apagada '
          : message.body}
      </MarkdownWrapper>
    );
  };

  const MEDIA_ONLY_TYPES = new Set([
    'audio',
    'locationMessage',
    'contactMessage',
    'pollCreationMessageV3',
    'eventMessage',
    'listMessage',
    'viewOnceMessage',
    'interactiveMessage',
    'adMetaPreview',
  ]);

  const shouldShowBodyText = (message) => {
    if (MEDIA_ONLY_TYPES.has(message.mediaType)) return false;
    if (
      (message.mediaType === 'image' || message.mediaType === 'video') &&
      message.mediaUrl &&
      getBasename(message.mediaUrl).trim() === message.body?.trim()
    ) {
      return false;
    }
    return true;
  };

  const renderMessages = () => {
    if (messagesList.length === 0) {
      return (
        <div
          style={{
            display: 'flex',
            width: '100%',
            heigth: '100%',
            alignItems: 'center',
            justifyContent: 'center',
            opacity: 0.5,
          }}
        >
          Diga olá para seu novo contato!
        </div>
      );
    }

    return messagesList.map((message, index) => {
      if (message.mediaType === 'call_log') {
        return (
          <React.Fragment key={message.id}>
            {renderDailyTimestamps(message, index)}
            {renderTicketsSeparator(message, index)}
            {renderMessageDivider(message, index)}
            <div style={classes.messageCenter}>
              {isGroup && (
                <span style={classes.messageContactName}>
                  {message.contact?.name}
                </span>
              )}
              <div>
                <svg
                  xmlns='http://www.w3.org/2000/svg'
                  viewBox='0 0 20 17'
                  width='20'
                  height='17'
                >
                  <path
                    fill='#df3333'
                    d='M18.2 12.1c-1.5-1.8-5-2.7-8.2-2.7s-6.7 1-8.2 2.7c-.7.8-.3 2.3.2 2.8.2.2.3.3.5.3 1.4 0 3.6-.7 3.6-.7.5-.2.8-.5.8-1v-1.3c.7-1.2 5.4-1.2 6.4-.1l.1.1v1.3c0 .2.1.4.2.6.1.2.3.3.5.4 0 0 2.2.7 3.6.7.2 0 1.4-2 .5-3.1zM5.4 3.2l4.7 4.6 5.8-5.7-.9-.8L10.1 6 6.4 2.3h2.5V1H4.1v4.8h1.3V3.2z'
                  />
                </svg>{' '}
                <span>
                  {i18n.t('ticketsList.missedCall')}{' '}
                  {format(parseISO(message.createdAt), 'HH:mm')}
                </span>
              </div>
            </div>
          </React.Fragment>
        );
      }

      const hasReaction = fetchHasReaction(message);
      const isHovered = hoveredMessageId === message.id;

      if (!message.fromMe) {
        return (
          <React.Fragment key={message.id}>
            {renderDailyTimestamps(message, index)}
            {renderTicketsSeparator(message, index)}
            {renderMessageDivider(message, index)}
            <div
              ref={(el) => {
                if (el) messageRef.current[message.id] = el;
              }}
              style={{
                ...classes.messageLeft,
                marginBottom: hasReaction ? '11px' : '0',
              }}
              title={message.queueId && message.queue?.name}
              onDoubleClick={() => handleReplyMessage(message)}
              onMouseOver={() => setHoveredMessageId(message.id)}
              onMouseOut={() => setHoveredMessageId(null)}
            >
              <MessageReactions message={message} position='left' />

              <IconButton
                variant='outlined'
                size='small'
                disabled={message.isDeleted}
                sx={
                  isHovered
                    ? classes.messageActionsLeft
                    : classes.messageActionsButtonInactiveLeft
                }
                onClick={(e) => handleOpenMessageOptionsMenu(e, message)}
              >
                <ExpandMore />
              </IconButton>

              {message.isForwarded && (
                <div>
                  <span style={classes.forwardMessage}>
                    <Reply sx={{ color: 'grey', transform: 'scaleX(-1)' }} />{' '}
                    Encaminhada
                  </span>
                  <br />
                </div>
              )}

              {isGroup && (
                <span style={classes.messageContactName}>
                  {message.contact?.name}
                </span>
              )}

              {isYouTubeLink(message.body) && (
                <YouTubePreview videoUrl={message.body} />
              )}

              {!lgpdDeleteMessage && message.isDeleted && (
                <div>
                  <Block
                    sx={{
                      transform: 'scale(0.8)',
                      margin: '0 2px 2px 0',
                      color: '#b8b8b8',
                    }}
                  />
                  <span style={classes.deletedMessage}>
                    Essa mensagem foi apagada pelo contato &nbsp;
                  </span>
                </div>
              )}

              {!message.isDeleted && (
                <div
                  style={{
                    ...classes.textContentItem,
                    ...(message.isDeleted
                      ? classes.textContentItemDeleted
                      : {}),
                  }}
                >
                  {showSelectMessageCheckbox && (
                    <SelectMessageCheckbox message={message} />
                  )}
                  {message.quotedMsg && renderQuotedMessage(message)}

                  {(message.mediaUrl ||
                    message.mediaType === 'locationMessage' ||
                    message.mediaType === 'contactMessage' ||
                    message.mediaType === 'pollCreationMessageV3' ||
                    message.mediaType === 'eventMessage' ||
                    message.mediaType === 'listMessage' ||
                    message.mediaType === 'viewOnceMessage' ||
                    message.mediaType === 'interactiveMessage' ||
                    message.mediaType === 'adMetaPreview') &&
                    checkMessageMedia(message)}

                  {shouldShowBodyText(message) &&
                    renderMessageText(message, false)}

                  {message.quotedMsg &&
                    message.mediaType === 'reactionMessage' && (
                      <span style={{ marginLeft: '0px' }}>
                        <MarkdownWrapper>
                          {`${message?.contact?.name} reagiu... ${message.body}`}
                        </MarkdownWrapper>
                      </span>
                    )}

                  <span style={classes.timestamp}>
                    {message.isEdited
                      ? `Editada ${format(parseISO(message.createdAt), 'HH:mm')}`
                      : format(parseISO(message.createdAt), 'HH:mm')}
                  </span>
                </div>
              )}
            </div>
          </React.Fragment>
        );
      }

      return (
        <React.Fragment key={message.id}>
          {renderDailyTimestamps(message, index)}
          {renderTicketsSeparator(message, index)}
          {renderMessageDivider(message, index)}
          <div
            ref={(el) => {
              if (el) messageRef.current[message.id] = el;
            }}
            style={{
              ...(message.isPrivate
                ? classes.messageRightPrivate
                : classes.messageRight),
              marginBottom: hasReaction ? '11px' : '0',
              paddingLeft: isHovered ? 35 : 5,
              maxWidth: isHovered ? 430 : 400,
            }}
            title={message.queueId && message.queue?.name}
            onDoubleClick={() => handleReplyMessage(message)}
            onMouseOver={() =>
              !message.isPrivate && setHoveredMessageId(message.id)
            }
            onMouseOut={() => !message.isPrivate && setHoveredMessageId(null)}
          >
            <MessageReactions message={message} position='right' />

            {!message.isPrivate && (
              <IconButton
                variant='outlined'
                size='small'
                id='messageActionsButton'
                disabled={message.isDeleted}
                sx={
                  isHovered
                    ? classes.messageActionsRight
                    : classes.messageActionsButtonInactiveRight
                }
                onClick={(e) => handleOpenMessageOptionsMenu(e, message)}
              >
                <ExpandMore />
              </IconButton>
            )}

            {message.isForwarded && (
              <div>
                <span style={classes.forwardMessage}>
                  <Reply style={{ color: 'grey', transform: 'scaleX(-1)' }} />{' '}
                  Encaminhada
                </span>
                <br />
              </div>
            )}

            {isYouTubeLink(message.body) && (
              <YouTubePreview videoUrl={message.body} />
            )}

            {!lgpdDeleteMessage && message.isDeleted && (
              <div>
                <Block
                  sx={{
                    transform: 'scale(0.8)',
                    margin: '0 2px 2px 0',
                    color: '#b8b8b8',
                  }}
                />
                <span style={classes.deletedMessage}>
                  Essa mensagem foi apagada &nbsp;
                </span>
              </div>
            )}

            {!message.isDeleted && (
              <div
                style={{
                  ...classes.textContentItem,
                  ...(message.isDeleted ? classes.textContentItemDeleted : {}),
                }}
              >
                {showSelectMessageCheckbox && (
                  <SelectMessageCheckbox message={message} />
                )}
                {message.quotedMsg && renderQuotedMessage(message)}

                {(message.mediaUrl ||
                  message.mediaType === 'locationMessage' ||
                  message.mediaType === 'contactMessage' ||
                  message.mediaType === 'pollCreationMessageV3' ||
                  message.mediaType === 'eventMessage' ||
                  message.mediaType === 'listMessage' ||
                  message.mediaType === 'viewOnceMessage' ||
                  message.mediaType === 'interactiveMessage' ||
                  message.mediaType === 'adMetaPreview') &&
                  checkMessageMedia(message)}

                {shouldShowBodyText(message) &&
                  !XML_REGEX.test(message.body) &&
                  renderMessageText(message, true)}

                {message.quotedMsg &&
                  message.mediaType === 'reactionMessage' && (
                    <span style={{ marginLeft: '0px' }}>
                      <MarkdownWrapper>
                        {`Você reagiu... ${message.body}`}
                      </MarkdownWrapper>
                    </span>
                  )}

                <span style={classes.timestamp}>
                  {message.isEdited
                    ? `Editada ${format(parseISO(message.createdAt), 'HH:mm')}`
                    : format(parseISO(message.createdAt), 'HH:mm')}
                  {renderMessageAck(message)}
                </span>
              </div>
            )}
          </div>
        </React.Fragment>
      );
    });
  };

  return (
    <div style={classes.messagesListWrapper} onDragEnter={handleDrag}>
      {dragActive && (
        <div
          style={classes.dragElement}
          onDragEnter={handleDrag}
          onDragLeave={handleDrag}
          onDragOver={handleDrag}
          onDrop={handleDrop}
        >
          Solte o arquivo aqui
        </div>
      )}

      <MessageOptionsMenu
        message={selectedMessage}
        anchorEl={anchorEl}
        menuOpen={messageOptionsMenuOpen}
        handleClose={handleCloseMessageOptionsMenu}
        isGroup={isGroup}
        whatsappId={whatsappId}
        queueId={queueId}
        ticketId={ticketId}
      />

      <MessageReactionsModal
        open={showReactionSender}
        onClose={handleCloseReactionModal}
        message={selectedMessageForReactions}
        user={user}
      />

      <Box id='messagesList' sx={classes.messagesList} onScroll={handleScroll}>
        {renderMessages()}
      </Box>

      {channel !== 'whatsapp' && channel !== undefined && (
        <div
          style={{
            width: '100%',
            display: 'flex',
            padding: '10px',
            alignItems: 'center',
            backgroundColor: '#E1F3FB',
          }}
        >
          {channel === 'facebook' ? <Facebook /> : <Instagram />}
          <span>
            Você tem 24h para responder após receber uma mensagem, de acordo com
            as políticas do Facebook.
          </span>
        </div>
      )}

      {loading && (
        <div style={classes.divLoading}>
          <CircularProgress sx={classes.circleLoading} />
        </div>
      )}
    </div>
  );
};

export default MessagesList;
